import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Sparkles, Activity, MapPin, Calendar, RefreshCw, Download,
  ChevronDown, Trophy, Users, AlertTriangle, FileText, Stethoscope, Briefcase,
  DollarSign, X, CheckCircle2, ArrowDownRight, ArrowUpRight, Bot, Wand2,
  ChevronLeft, Building2,
} from "lucide-react";
import {
  CP_PERIOD, CP_COMPARE, CP_LOCATIONS, CP_SIGNALS, CP_APPTS, CP_REVENUE,
  CP_PRACT, CP_FRANCHISE, CP_ASK_AI, CP_LOCATION_SUMMARY,
} from "@/config/clinicPulseMock";
import { findEmrBySlug } from "@/config/emrs";
import { findEmrPage } from "@/config/emrPages";
import { EmrDemoForm } from "@/components/landing/EmrDemoForm";
import { ModalShell } from "@/components/landing/ModalShell";

/* /clinic-pulse — Interactive walkthrough showing a multi-location Juvonno
   group owner what they'd see inside Clinic OS Executive Reporting (Clinic Pulse).
   Mock data only — clinic names and numbers are illustrative. */

const TONES = {
  win:    { bg: "bg-emerald-500/10",  border: "border-emerald-500/30",  text: "text-emerald-700 dark:text-emerald-300", chipBg: "bg-emerald-500/20",  chipText: "text-emerald-700 dark:text-emerald-300", iconBg: "bg-emerald-500", Icon: Trophy },
  watch:  { bg: "bg-amber-500/10",    border: "border-amber-500/30",    text: "text-amber-700 dark:text-amber-300",     chipBg: "bg-amber-500/20",    chipText: "text-amber-700 dark:text-amber-300",     iconBg: "bg-amber-500",   Icon: AlertTriangle },
  action: { bg: "bg-rose-500/10",     border: "border-rose-500/30",     text: "text-rose-700 dark:text-rose-300",       chipBg: "bg-rose-500/20",     chipText: "text-rose-700 dark:text-rose-300",       iconBg: "bg-rose-500",    Icon: X },
};

const TAB_ORDER = ["appts", "revenue", "pract", "franchise", "leads"];
const TAB_LABELS = { appts: "Appointments", revenue: "Revenue", pract: "Practitioners", franchise: "Franchise", leads: "Leads" };

export default function ClinicPulse() {
  const [tab, setTab] = useState("appts");
  const [showAskAi, setShowAskAi] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const juvonnoEmr  = findEmrBySlug("juvonno");
  const juvonnoPage = findEmrPage("juvonno");

  const nextTabId = TAB_ORDER[(TAB_ORDER.indexOf(tab) + 1) % TAB_ORDER.length];
  const cycleTab = () => {
    setTab(nextTabId);
    const tabsEl = document.querySelector('[data-testid="pulse-tabs"]');
    if (tabsEl) tabsEl.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const maxAppts = Math.max(...CP_APPTS.byLocation.map((b) => b.apts));
  const maxRev   = Math.max(...CP_REVENUE.byLocation.map((b) => b.rev));
  const maxP     = Math.max(...CP_PRACT.table.map((b) => b.p));
  const maxFrApts = Math.max(...CP_FRANCHISE.table.map((f) => f.apts));

  return (
    <article data-testid="clinic-pulse-page" className="overflow-hidden">

      {/* ───────────── HERO ───────────── */}
      <section className="relative pt-16 pb-8 sm:pt-32 sm:pb-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[480px] bg-gradient-to-b from-violet-500/15 via-fuchsia-500/8 to-transparent" />

        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <Link
            to="/integrations/juvonno"
            data-testid="pulse-back"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="h-3 w-3" />
            Back to Juvonno integration
          </Link>

          <div className="mt-4 sm:mt-6 grid items-start gap-6 sm:gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
                <Activity className="h-3 w-3" />
                Clinic Pulse · Walkthrough
              </span>
              <h1 className="mt-0 sm:mt-5 font-display text-[2.05rem] font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-[3.5rem]">
                What multi-location Juvonno owners{" "}
                <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500 bg-clip-text text-transparent">actually see.</span>
              </h1>
              <p className="mt-3 sm:mt-5 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg">
                Scroll through the exact executive dashboard your group owners get the day Clinic OS goes live. Every signal, every chart, every AI summary — pulled live from your Juvonno account, no spreadsheets in the middle.
              </p>
              <div className="mt-5 sm:mt-7 flex flex-wrap items-center gap-2 sm:gap-3">
                <a
                  href="#dashboard"
                  data-testid="pulse-hero-cta"
                  className="group inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                >
                  See the dashboard
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                <button
                  type="button"
                  onClick={() => setShowDemo(true)}
                  data-testid="pulse-hero-book"
                  className="hidden sm:inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                >
                  Book a personalised walk-through
                </button>
              </div>
            </div>

            {/* Stat tile grid — desktop only; mobile users scroll directly to the dashboard mock */}
            <div className="hidden lg:col-span-5 lg:block">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {[
                  { k: "9 signals", v: "auto-detected per month",                ic: Sparkles,    fg: "from-violet-500 to-fuchsia-500" },
                  { k: "Ask AI",    v: "natural-language queries",               ic: Bot,         fg: "from-blue-500 to-cyan-500" },
                  { k: "Per-location", v: "AI executive summaries",              ic: Activity,    fg: "from-emerald-500 to-teal-500" },
                  { k: "PDF Export", v: "board-ready in one click",              ic: Download,    fg: "from-amber-500 to-orange-500" },
                ].map((c) => (
                  <div key={c.k} className="rounded-2xl border border-border bg-card p-5">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${c.fg} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                      <c.ic className="h-5 w-5" />
                    </span>
                    <p className="mt-3.5 font-display text-lg font-black tracking-tight">{c.k}</p>
                    <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{c.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* (PainPoints "data gap" and DiscChapters "Pulse, four ways" removed per request) */}

      {/* ───────────── EXECUTIVE DASHBOARD MOCK ───────────── */}
      <section id="dashboard" data-testid="pulse-dashboard" className="scroll-mt-24 border-t border-border bg-secondary/30 py-16 dark:bg-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          {/* Step caption */}
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
              Step 01 · The executive dashboard
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
              One screen. Every location. The whole month.
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Open Clinic Pulse and this is the first thing you see — filtered to whichever location, month or range you care about, with AI signals already surfaced for you.
            </p>
          </div>

          {/* The mock app frame */}
          <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-background shadow-2xl ring-1 ring-inset ring-foreground/5">

            {/* Top toolbar */}
            <div className="border-b border-border bg-card p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md ring-1 ring-inset ring-white/15">
                  <Activity className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-2xl font-black tracking-tight sm:text-3xl">
                    Executive Dashboard
                  </h3>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                    Business performance — total vs per-location breakdown
                  </p>
                </div>
              </div>

              {/* Filter row */}
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <FilterChip Icon={MapPin}    label="All Locations"  testid="pulse-filter-loc" />
                <FilterChip Icon={Calendar}  label={CP_PERIOD}      testid="pulse-filter-month" />
                <FilterChip Icon={Calendar}  label="Range"          testid="pulse-filter-range" outline />
                <FilterChip                   label={CP_COMPARE}     testid="pulse-filter-compare" outline />
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAskAi(true)}
                    data-testid="pulse-ask-ai-open"
                    className="group inline-flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-[12px] font-bold text-violet-700 transition-all hover:-translate-y-0.5 hover:bg-violet-500/15 dark:text-violet-300"
                  >
                    <Wand2 className="h-3.5 w-3.5" />
                    Ask AI
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSummary(true)}
                    data-testid="pulse-summary-open"
                    className="group inline-flex items-center gap-1.5 rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-[12px] font-bold text-blue-700 transition-all hover:-translate-y-0.5 hover:bg-blue-500/15 dark:text-blue-300"
                  >
                    <Activity className="h-3.5 w-3.5" />
                    Location Summary
                  </button>
                  <FilterChip Icon={RefreshCw} label="Refresh" testid="pulse-refresh" outline />
                  <FilterChip Icon={Download}  label="Export"  testid="pulse-export"  outline />
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className="border-b border-border p-5 sm:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="inline-flex items-center gap-1.5 font-display text-[15px] font-black tracking-tight text-violet-600 dark:text-violet-300">
                  <Sparkles className="h-4 w-4" />
                  AI Insights
                  <span className="ml-1 text-[11.5px] font-semibold text-muted-foreground">· {CP_SIGNALS.length} signals detected</span>
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Signal Guide</p>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {CP_SIGNALS.map((s, i) => {
                  const t = TONES[s.tone];
                  return (
                    <motion.div
                      key={`${s.title}-${i}`}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.32, delay: (i % 3) * 0.04 }}
                      data-testid={`pulse-signal-${i}`}
                      className={`relative overflow-hidden rounded-2xl border ${t.border} ${t.bg} p-4`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ${t.iconBg} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                          <t.Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="font-display text-[14px] font-black tracking-tight">{s.title}</p>
                            <span className={`rounded-md px-1.5 py-0.5 text-[9.5px] font-bold ${t.chipBg} ${t.chipText}`}>{s.chip}</span>
                          </div>
                          <p className="mt-1 text-[12.5px] font-semibold leading-snug">{s.body}</p>
                          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{s.foot}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Tab strip — highlighted with pulsing glow + hint to draw attention */}
            <div className="relative border-b border-border bg-gradient-to-r from-violet-500/[0.10] via-fuchsia-500/[0.10] to-rose-500/[0.10] px-5 py-4 sm:px-6">
              {/* Animated outer ring glow */}
              <motion.div
                aria-hidden
                animate={{ opacity: [0.4, 0.9, 0.4] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-violet-500 to-transparent"
              />
              <motion.div
                aria-hidden
                animate={{ opacity: [0.4, 0.9, 0.4] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-fuchsia-500 to-transparent"
              />

              {/* Attention hint */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="inline-flex items-center gap-2 text-[11.5px] font-bold tracking-tight text-violet-700 dark:text-violet-300">
                  <motion.span
                    animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="inline-flex h-2 w-2 flex-none rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-[0_0_8px_rgba(168,85,247,0.6)]"
                  />
                  <span className="uppercase tracking-[0.18em]">Tap each tab</span>
                  <span className="hidden text-foreground/70 sm:inline">— Appointments, Revenue, Practitioners, Franchise and Leads each tell a different story</span>
                </p>
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  5 views · 1 group
                </span>
              </div>

              {/* Tab buttons */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="pulse-tabs">
                {[
                  { id: "appts",     label: "Appointments",  Icon: Calendar },
                  { id: "revenue",   label: "Revenue",       Icon: DollarSign },
                  { id: "pract",     label: "Practitioners", Icon: Stethoscope },
                  { id: "franchise", label: "Franchise",     Icon: Building2 },
                  { id: "leads",     label: "Leads",         Icon: Briefcase },
                ].map((t) => {
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      data-testid={`pulse-tab-${t.id}`}
                      aria-pressed={active}
                      className={
                        active
                          ? "relative inline-flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-card px-3 py-1.5 text-[12px] font-bold shadow-sm ring-1 ring-inset ring-violet-500/20"
                          : "relative inline-flex items-center gap-1.5 rounded-lg border border-transparent px-3 py-1.5 text-[12px] font-semibold text-muted-foreground transition-colors hover:bg-card/60 hover:text-foreground"
                      }
                    >
                      <t.Icon className="h-3.5 w-3.5" />
                      {t.label}
                      {!active && (
                        <motion.span
                          aria-hidden
                          animate={{ scale: [1, 1.6, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute -right-0.5 -top-0.5 inline-flex h-1.5 w-1.5 rounded-full bg-fuchsia-500 shadow-[0_0_6px_rgba(217,70,239,0.7)]"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab body */}
            <div className="p-5 sm:p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                >
                  {tab === "appts" && (
                    <ApptsTab data={CP_APPTS} maxBar={maxAppts} />
                  )}
                  {tab === "revenue" && (
                    <RevenueTab data={CP_REVENUE} maxBar={maxRev} />
                  )}
                  {tab === "pract" && (
                    <PractTab data={CP_PRACT} maxBar={maxP} />
                  )}
                  {tab === "franchise" && (
                    <FranchiseTab data={CP_FRANCHISE} maxBar={maxFrApts} />
                  )}
                  {tab === "leads" && (
                    <LeadsTab />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Compact data-source caption */}
          <p className="mx-auto mt-6 max-w-2xl text-center text-[12px] leading-relaxed text-muted-foreground">
            Numbers above are mock data — on your account they&apos;re live from Juvonno.
          </p>
        </div>
      </section>

      {/* ───────────── ASK AI WALKTHROUGH ───────────── */}
      <section data-testid="pulse-ask-ai-section" className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
                Step 02 · Ask AI
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                Ask your business{" "}
                <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">a question.</span>
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                No more Excel pivots, no analyst tickets, no waiting for end-of-month. Type the question — get the answer, with the data and the recommended action, in seconds.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {[
                  "Trained on your live Juvonno data",
                  "Cross-references all locations + franchises at once",
                  "Returns actionable bullet points · not raw SQL",
                  "Every answer cites the underlying metric",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-violet-500" />
                    <span className="text-foreground/85">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              <AskAiPanel />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── LOCATION AI SUMMARY ───────────── */}
      <section data-testid="pulse-summary-section" className="border-t border-border bg-secondary/30 py-16 dark:bg-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-600 dark:text-blue-300">
              Step 03 · Location AI Summary
            </p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
              A board-ready report{" "}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">for any location.</span>
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Pick a location and a date range. The AI writes the executive summary, surfaces 5 performance signals, and exports board-ready PDF — in under 10 seconds.
            </p>
          </div>

          <div className="mt-10">
            <LocationSummaryCard />
          </div>
        </div>
      </section>

      {/* ───────────── CTA ───────────── */}
      <section data-testid="pulse-cta" className="border-t border-border py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            See <span className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-600 bg-clip-text text-transparent">your own</span> Juvonno data in Clinic Pulse.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            We&apos;ll connect your Juvonno account and walk you through the dashboard with your real numbers — no slides, no demo data.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setShowDemo(true)}
              data-testid="pulse-final-cta"
              className="group inline-flex h-12 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 text-sm font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              Book your Juvonno walk-through
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <Link
              to="/products/clinic-pulse"
              data-testid="pulse-read-product"
              className="inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card px-7 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
            >
              Read about Clinic Pulse
            </Link>
          </div>
        </div>
      </section>

      {/* ── Ask AI modal ───────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showAskAi && <AskAiModal onClose={() => setShowAskAi(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showSummary && <SummaryModal onClose={() => setShowSummary(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showDemo && juvonnoEmr && juvonnoPage && (
          <ModalShell onClose={() => setShowDemo(false)} wide>
            <div className="p-3 sm:p-4">
              <EmrDemoForm emr={juvonnoEmr} page={juvonnoPage} compact />
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </article>
  );
}

/* ============================================================================
   SUB-COMPONENTS
   ========================================================================= */

const FilterChip = ({ Icon, label, testid, outline = false }) => (
  <button
    type="button"
    data-testid={testid}
    className={
      outline
        ? "inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground/80 transition-all hover:-translate-y-0.5 hover:border-foreground/30"
        : "inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground/80 transition-all hover:-translate-y-0.5 hover:border-foreground/30"
    }
  >
    {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
    {label}
    {Icon && <ChevronDown className="h-3 w-3 opacity-50" />}
  </button>
);

/* ── Totals tile ─────────────────────────────────────────────────────────── */
const TINTS = {
  emerald:       { ring: "ring-emerald-500/15", text: "text-emerald-600 dark:text-emerald-400" },
  "emerald-soft":{ ring: "ring-emerald-500/10", text: "text-foreground" },
  rose:          { ring: "ring-rose-500/15",    text: "text-rose-600 dark:text-rose-400" },
  sky:           { ring: "ring-sky-500/15",     text: "text-sky-600 dark:text-sky-400" },
  amber:         { ring: "ring-amber-500/15",   text: "text-amber-600 dark:text-amber-400" },
  indigo:        { ring: "ring-indigo-500/15",  text: "text-indigo-600 dark:text-indigo-400" },
};

const TotalTile = ({ data }) => {
  const tint = TINTS[data.tint] || { ring: "ring-foreground/10", text: "text-foreground" };
  return (
    <div className={`rounded-2xl border border-border bg-card p-4 ring-1 ring-inset ${tint.ring}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{data.label}</p>
      <p className={`mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl ${tint.text}`}>{data.value}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
        {data.mom && (
          <span className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-bold ${data.mom.dir === "down" ? "bg-rose-500/15 text-rose-600 dark:text-rose-400" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
            MoM {data.mom.dir === "down" ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}{data.mom.val}
          </span>
        )}
        {data.yoy && (
          <span className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-bold ${data.yoy.dir === "down" ? "bg-rose-500/15 text-rose-600 dark:text-rose-400" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
            YoY {data.yoy.dir === "down" ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}{data.yoy.val}
          </span>
        )}
        {data.sub && <span className="text-muted-foreground">{data.sub}</span>}
      </div>
    </div>
  );
};

const SubKpi = ({ data }) => (
  <div className="rounded-xl border border-border bg-card p-3.5">
    <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{data.label}</p>
    <p className={`mt-1.5 font-display text-xl font-black tracking-tight ${data.accent === "indigo" ? "text-indigo-600 dark:text-indigo-400" : data.accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"}`}>
      {data.value}
    </p>
    {data.sub && <p className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">{data.sub}</p>}
    {data.action && <p className={`mt-0.5 text-[9.5px] ${data.accent === "indigo" ? "text-indigo-600 dark:text-indigo-400" : "text-emerald-600 dark:text-emerald-400"}`}>{data.action}</p>}
  </div>
);

/* ── Bar chart by location ───────────────────────────────────────────────── */
const LocationBars = ({ items, max, valueKey = "apts", format = (v) => v }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Appointments by Location</p>
    <div className="mt-4 flex h-56 items-stretch gap-2">
      {items.map((b) => {
        const loc = CP_LOCATIONS.find((l) => l.name === b.loc);
        const h = Math.round((b[valueKey] / max) * 100);
        return (
          <div key={b.loc} className="flex h-full flex-1 flex-col items-center justify-end">
            <span className="mb-1 text-[9.5px] font-semibold text-muted-foreground">{format(b[valueKey])}</span>
            <div
              className="w-full rounded-t-md transition-all hover:opacity-80"
              style={{ height: `${h}%`, backgroundColor: loc?.color || "#999" }}
              title={`${b.loc} · ${format(b[valueKey])}`}
            />
            <span className="mt-1.5 truncate text-[10px] font-semibold text-foreground/80" style={{ maxWidth: "100%" }}>{b.loc}</span>
          </div>
        );
      })}
    </div>
  </div>
);

/* ── Appointments tab ───────────────────────────────────────────────────── */
const ApptsTab = ({ data, maxBar }) => (
  <div>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {data.totals.map((t) => <TotalTile key={t.label} data={t} />)}
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
      {data.subKpis.map((s, i) => <SubKpi key={i} data={s} />)}
    </div>

    <div className="mt-5 grid gap-3 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <LocationBars items={data.byLocation} max={maxBar} valueKey="apts" format={(v) => v.toLocaleString()} />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Assessments &amp; Treatments</p>
        <p className="mt-1 text-[11px] text-muted-foreground">Scheduled / Showed-Up per location</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            { k: "Assessments", v: "1,084", sub: "Non-cancelled 921", accent: "indigo" },
            { k: "Treatments",  v: "11,750",sub: "Non-cancelled 9,641", accent: "indigo" },
          ].map((b) => (
            <div key={b.k} className={`rounded-xl border border-border bg-indigo-500/[0.06] p-3`}>
              <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-indigo-600 dark:text-indigo-400">{b.k}</p>
              <p className="mt-1.5 font-display text-xl font-black tracking-tight">Scheduled</p>
              <p className="mt-0.5 font-display text-lg font-black tracking-tight text-indigo-600 dark:text-indigo-400">{b.v}</p>
              <p className="mt-1 text-[10px] text-muted-foreground">{b.sub}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5 text-[11.5px]">
          {data.assessTreat.map((r) => (
            <div key={r.loc} className="flex items-center justify-between border-b border-border/60 pb-1">
              <span className="font-semibold">{r.loc}</span>
              <span className="text-muted-foreground">
                <span className="font-mono text-indigo-600 dark:text-indigo-400">{r.ax}</span> Ax{" "}
                <span className="font-mono text-emerald-600 dark:text-emerald-400">{r.tx}</span> Tx
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ── Revenue tab ────────────────────────────────────────────────────────── */
const RevenueTab = ({ data, maxBar }) => (
  <div>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {data.totals.map((t) => <TotalTile key={t.label} data={t} />)}
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {data.subKpis.map((s, i) => <SubKpi key={i} data={s} />)}
    </div>

    <div className="mt-5 grid gap-3 lg:grid-cols-3">
      <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Revenue by Location</p>
        <div className="mt-4 flex h-56 items-stretch gap-2">
          {data.byLocation.map((b) => {
            const loc = CP_LOCATIONS.find((l) => l.name === b.loc);
            const h = Math.round((b.rev / maxBar) * 100);
            return (
              <div key={b.loc} className="flex h-full flex-1 flex-col items-center justify-end">
                <span className="mb-1 text-[9.5px] font-semibold text-muted-foreground">${(b.rev / 1000).toFixed(0)}K</span>
                <div
                  className="w-full rounded-t-md transition-all hover:opacity-80"
                  style={{ height: `${h}%`, backgroundColor: loc?.color || "#999" }}
                />
                <span className="mt-1.5 truncate text-[10px] font-semibold text-foreground/80">{b.loc}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Collection Rate</p>
        <div className="mt-4 space-y-2.5">
          {data.byLocation.map((b) => (
            <div key={b.loc}>
              <div className="flex items-center justify-between text-[11px] font-semibold">
                <span>{b.loc}</span>
                <span className="font-mono">{b.collect}%</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
                <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" style={{ width: `${b.collect}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ── Practitioners tab ─────────────────────────────────────────────────── */
const PractTab = ({ data, maxBar }) => (
  <div>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {data.totals.map((t) => <TotalTile key={t.label} data={t} />)}
    </div>

    <div className="mt-5 overflow-hidden rounded-2xl border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="border-b border-border bg-secondary/40 text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
            <tr>
              {["Location","Practitioners","Appointments","Avg/Practitioner","Patients","Revenue","Rev/Practitioner"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.table.map((r) => (
              <tr key={r.loc} className="border-b border-border/60 transition-colors hover:bg-secondary/30">
                <td className="px-4 py-2.5 font-semibold">{r.loc}</td>
                <td className="px-4 py-2.5">{r.p}</td>
                <td className="px-4 py-2.5">{r.apts.toLocaleString()}</td>
                <td className="px-4 py-2.5">{r.avg}</td>
                <td className="px-4 py-2.5">{r.pts}</td>
                <td className="px-4 py-2.5 font-semibold">{r.rev}</td>
                <td className="px-4 py-2.5 font-semibold">{r.rpp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <div className="mt-5">
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Practitioners per Location</p>
        <div className="mt-4 flex h-44 items-stretch gap-2">
          {data.table.map((b) => {
            const loc = CP_LOCATIONS.find((l) => l.name === b.loc);
            const h = Math.round((b.p / maxBar) * 100);
            return (
              <div key={b.loc} className="flex h-full flex-1 flex-col items-center justify-end">
                <span className="mb-1 text-[9.5px] font-semibold text-muted-foreground">{b.p}</span>
                <div className="w-full rounded-t-md transition-all hover:opacity-80" style={{ height: `${h}%`, backgroundColor: loc?.color || "#999" }} />
                <span className="mt-1.5 truncate text-[10px] font-semibold text-foreground/80">{b.loc}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
);

/* ── Franchise tab ─────────────────────────────────────────────────────── */
const FranchiseTab = ({ data, maxBar }) => {
  const compTone = {
    win:   { bg: "bg-emerald-500/15", text: "text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
    watch: { bg: "bg-amber-500/15",   text: "text-amber-600 dark:text-amber-400",     bar: "bg-amber-500" },
  };
  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.totals.map((t) => <TotalTile key={t.label} data={t} />)}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.subKpis.map((s, i) => <SubKpi key={i} data={s} />)}
      </div>

      {/* Franchise cards */}
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {data.table.map((f) => {
          const tone = compTone[f.compTone] || compTone.win;
          return (
            <div
              key={f.name}
              data-testid={`pulse-franchise-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-lg"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm ring-1 ring-inset ring-white/15">
                  <Building2 className="h-5 w-5" />
                </span>
                <span className={`rounded-md ${tone.bg} px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${tone.text}`}>
                  {f.compliance}% compliance
                </span>
              </div>
              <p className="mt-3.5 font-display text-xl font-black tracking-tight">{f.name}</p>
              <p className="mt-0.5 text-[11.5px] text-muted-foreground">{f.locs}</p>

              <div className="mt-4 grid grid-cols-2 gap-2 text-[11.5px]">
                <div className="rounded-lg border border-border bg-secondary/30 p-2.5">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Practitioners</p>
                  <p className="mt-1 font-display text-base font-black tracking-tight">{f.pract}</p>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-2.5">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Appointments</p>
                  <p className="mt-1 font-display text-base font-black tracking-tight">{f.apts.toLocaleString()}</p>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-2.5">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Revenue · MTD</p>
                  <p className="mt-1 font-display text-base font-black tracking-tight">{f.revenue}</p>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-2.5">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Royalty · {f.royaltyRate}</p>
                  <p className="mt-1 font-display text-base font-black tracking-tight text-emerald-600 dark:text-emerald-400">{f.royalty}</p>
                </div>
              </div>

              {/* Compliance meter */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-[10.5px] font-semibold">
                  <span className="text-muted-foreground">Brand compliance</span>
                  <span className="font-mono">{f.compliance}%</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-border">
                  <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${f.compliance}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bar — apts per franchise */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Appointments per Franchise</p>
        <div className="mt-4 flex h-40 items-stretch gap-3">
          {data.table.map((f, i) => {
            const h = Math.round((f.apts / maxBar) * 100);
            const colors = ["#7c3aed", "#ec4899", "#0d9488"];
            return (
              <div key={f.name} className="flex h-full flex-1 flex-col items-center justify-end">
                <span className="mb-1 text-[9.5px] font-semibold text-muted-foreground">{f.apts.toLocaleString()}</span>
                <div className="w-full rounded-t-md transition-all hover:opacity-80" style={{ height: `${h}%`, backgroundColor: colors[i] }} />
                <span className="mt-1.5 truncate text-[10.5px] font-semibold text-foreground/80">{f.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── Leads tab (lightweight) ───────────────────────────────────────────── */
const LeadsTab = () => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    {[
      { k: "New Leads",        v: "412",  s: "Across 5 locations" },
      { k: "Lead → Booked",    v: "61%",  s: "Industry avg 38%" },
      { k: "Booked → First Visit", v: "84%",  s: "Strong attach rate" },
      { k: "Cost per Acquired", v: "$38", s: "Down from $52 last month" },
    ].map((b) => (
      <div key={b.k} className="rounded-2xl border border-border bg-card p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{b.k}</p>
        <p className="mt-2 font-display text-3xl font-black tracking-tight">{b.v}</p>
        <p className="mt-1 text-[11.5px] text-muted-foreground">{b.s}</p>
      </div>
    ))}
  </div>
);

/* ── Ask AI panel (inline) ─────────────────────────────────────────────── */
const AskAiPanel = () => (
  <div className="overflow-hidden rounded-3xl border border-violet-500/30 bg-card shadow-xl">
    <div className="flex items-center gap-3 border-b border-border bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 px-5 py-3.5">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm ring-1 ring-inset ring-white/15">
        <Wand2 className="h-4 w-4" />
      </span>
      <div>
        <p className="font-display text-[14px] font-black tracking-tight">Ask AI</p>
        <p className="text-[11px] text-muted-foreground">Powered by your live Juvonno data</p>
      </div>
      <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Online
      </span>
    </div>

    <div className="p-5">
      {/* User message */}
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2.5 text-[13px] font-semibold text-white shadow-md">
          {CP_ASK_AI.user}
        </div>
      </div>

      {/* AI response */}
      <div className="mt-4 flex justify-start">
        <div className="max-w-[92%] rounded-2xl rounded-tl-sm border border-border bg-secondary/30 px-4 py-3.5 text-[13px] leading-relaxed">
          <p className="text-foreground/90">{CP_ASK_AI.ai.summary}</p>
          <div className="mt-3 space-y-3">
            {CP_ASK_AI.ai.sections.map((s) => (
              <div key={s.heading}>
                <p className="font-display text-[13px] font-bold tracking-tight">{s.heading}</p>
                <ul className="mt-1.5 space-y-1">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12.5px] text-foreground/85">
                      <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-violet-500" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ── Location Summary card (full inline) ───────────────────────────────── */
const LocationSummaryCard = () => {
  const d = CP_LOCATION_SUMMARY;
  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
      {/* Top toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-secondary/30 px-5 py-3.5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-cyan-600 text-white shadow-sm ring-1 ring-inset ring-white/15">
          <Activity className="h-4 w-4" />
        </span>
        <div>
          <p className="font-display text-[14px] font-black tracking-tight">Location AI Summary</p>
          <p className="text-[11px] text-muted-foreground">AI-powered performance analysis for any location &amp; date range</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <FilterChip Icon={Download} label="Export PDF" testid="ls-export" outline />
          <FilterChip Icon={FileText}  label="New Analysis" testid="ls-new" outline />
        </div>
      </div>

      {/* Dark banner */}
      <div className="bg-[#0a0a14] px-5 py-5 text-white sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-3xl font-black tracking-tight sm:text-4xl">{d.location}</h3>
            <p className="mt-1 text-[11.5px] text-white/60">{d.period} · {d.months} months analyzed</p>
          </div>
          <div className="flex gap-2">
            <div className="rounded-xl border border-white/15 bg-white/[0.06] px-3.5 py-2 text-right">
              <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/60">Total Revenue</p>
              <p className="font-display text-xl font-black tracking-tight text-emerald-400">{d.revenue}</p>
            </div>
            <div className="rounded-xl border border-white/15 bg-white/[0.06] px-3.5 py-2 text-right">
              <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/60">Collection</p>
              <p className="font-display text-xl font-black tracking-tight text-emerald-400">{d.collectionRate}</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { l: "Total Revenue",   v: d.revenue,         s: d.collected,      t: "emerald" },
          { l: "Collection Rate", v: d.collectionRate,  s: `${d.months} months avg`, t: "indigo" },
          { l: "Appointments",    v: d.appointments,    s: d.assessments,    t: "sky" },
          { l: "Issues",          v: d.issues,          s: d.issuesBreakdown, t: "rose" },
        ].map((b) => {
          const tint = TINTS[b.t];
          return (
            <div key={b.l} className={`rounded-2xl border border-border bg-card p-4 ring-1 ring-inset ${tint.ring}`}>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{b.l}</p>
              <p className={`mt-1.5 font-display text-3xl font-black tracking-tight ${tint.text}`}>{b.v}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{b.s}</p>
            </div>
          );
        })}
      </div>

      {/* Signals */}
      <div className="px-5 pb-5">
        <p className="inline-flex items-center gap-1.5 font-display text-[14px] font-black tracking-tight text-violet-600 dark:text-violet-300">
          <Sparkles className="h-4 w-4" />
          Performance Signals
          <span className="ml-1 text-[11px] font-semibold text-muted-foreground">· {d.signals.length} signals detected</span>
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {d.signals.map((s, i) => {
            const t = TONES[s.tone];
            return (
              <div key={i} className={`rounded-2xl border ${t.border} ${t.bg} p-4`}>
                <div className="flex items-start gap-3">
                  <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ${t.iconBg} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                    <t.Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="font-display text-[14px] font-black tracking-tight">{s.title}</p>
                      <span className={`rounded-md px-1.5 py-0.5 text-[9.5px] font-bold ${t.chipBg} ${t.chipText}`}>{s.chip}</span>
                    </div>
                    <p className="mt-1 text-[12.5px] font-semibold leading-snug">{s.body}</p>
                    <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{s.foot}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Executive Summary */}
      <div className="border-t border-border bg-violet-500/[0.04] px-5 py-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm ring-1 ring-inset ring-white/15">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <p className="font-display text-[14px] font-black tracking-tight">AI Executive Summary</p>
            <p className="text-[11px] text-muted-foreground">{d.location} · {d.period}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            AI Powered
          </span>
        </div>
        <p className="mt-3 text-[13.5px] leading-relaxed text-foreground/85">{d.aiSummary}</p>
      </div>

      {/* Key Performance Notes */}
      <div className="border-t border-border px-5 py-5">
        <p className="inline-flex items-center gap-1.5 font-display text-[14px] font-black tracking-tight text-violet-600 dark:text-violet-300">
          <Activity className="h-4 w-4" />
          Key Performance Notes
        </p>
        <ul className="mt-3 space-y-1.5">
          {d.notes.map((n) => (
            <li key={n} className="flex items-start gap-2 text-[12.5px] leading-snug text-foreground/85">
              <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-emerald-500" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ── Modals (lightweight — show inline component in modal frame) ─────── */

const AskAiModal = ({ onClose }) => (
  <ModalShell onClose={onClose} title="Ask AI">
    <div className="p-5 sm:p-6">
      <AskAiPanel />
    </div>
  </ModalShell>
);

const SummaryModal = ({ onClose }) => (
  <ModalShell onClose={onClose} title="Location Summary">
    <div className="p-3 sm:p-5">
      <LocationSummaryCard />
    </div>
  </ModalShell>
);
